import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, Shield, Zap, Gift, Bell } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PremiumPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar';

  const t = {
    en: {
      title: 'Premium Subscription',
      subtitle: 'Free members miss 70% of deals... Become a Premium member to receive all cheap flights!',
      premiumPlan: 'Premium Plan',
      price: '35 OMR',
      perYear: '/year',
      billedAnnually: '(Billed annually)',
      moneyBackGuarantee: '*30-Day Money-Back Guarantee*',
      moneyBackDesc: "If you're not satisfied with our service, we offer a full refund within the first 30 days. There's no risk in trying.",
      getStarted: 'Get All the Deals',
      whyPremium: 'Why Go Premium?',
      receiveAll: 'Receive',
      allDeals: 'ALL',
      theDeals: 'the Deals',
      receiveAllDesc: 'Premium members receive **100% of deals** while free members only get a small sample (30%). If there\'s a deal to your dream destination, you can be sure we\'ll send it to you!',
      earlyAccess: 'Additionally, Premium members get deals **several hours before free members**. Knowing that our deals sometimes last only a day, this is an advantage that will ensure you can take advantage!',
      savingsTitle: 'Save an average of 200 OMR per ticket',
      savingsDesc: 'Our deals allow an average savings of 200 OMR per plane ticket. Buy one ticket and the Premium subscription literally pays for itself (and even 10x more)! Savings are even greater when traveling with friends or family.',
      happyMembers: 'Over 500+ Happy Members',
      testimonial1: '"Saved 300 OMR on my London flight! Premium membership paid for itself in the first month."',
      testimonial2: '"Best investment for frequent travelers. The early access to deals is a game-changer!"',
      testimonial3: '"Weekly alerts keep me updated on all the best prices. Highly recommend Premium!"',
      guaranteeTitle: '30-Day Money-Back Guarantee',
      guaranteeDesc: 'If for any reason you\'re not happy with your Premium subscription, we offer a full refund within the first 30 days. There\'s no risk in trying Premium!',
      dontMiss: "Don't miss the perfect deal",
      dontMissDesc: 'Stop wasting hours searching for plane tickets. Let us find the best prices for your dream trip.',
      features: [
        { icon: Bell, text: 'Weekly flight deal alerts' },
        { icon: Star, text: 'Access to 100% of private deals' },
        { icon: Zap, text: 'Early access - Hours before free members' },
        { icon: Gift, text: 'Exclusive member discounts' },
        { icon: Shield, text: 'Priority WhatsApp support' }
      ]
    },
    ar: {
      title: 'الاشتراك المميز',
      subtitle: 'الأعضاء المجانيون يفوتون 70% من الصفقات... كن عضواً مميزاً لتلقي جميع رحلات الطيران الرخيصة!',
      premiumPlan: 'الخطة المميزة',
      price: '35 ريال',
      perYear: '/سنوياً',
      billedAnnually: '(يتم الدفع سنوياً)',
      moneyBackGuarantee: '*ضمان استرداد الأموال لمدة 30 يوماً*',
      moneyBackDesc: 'إذا لم تكن راضياً عن خدمتنا، نقدم استرداد كامل خلال أول 30 يوماً. لا يوجد أي مخاطرة في التجربة.',
      getStarted: 'احصل على جميع الصفقات',
      whyPremium: 'لماذا الخطة المميزة؟',
      receiveAll: 'استقبل',
      allDeals: 'جميع',
      theDeals: 'الصفقات',
      receiveAllDesc: 'يتلقى الأعضاء المميزون **100% من الصفقات** بينما يحصل الأعضاء المجانيون على عينة صغيرة فقط (30%). إذا كانت هناك صفقة إلى وجهتك المفضلة، يمكنك التأكد من أننا سنرسلها لك!',
      earlyAccess: 'بالإضافة إلى ذلك، يحصل الأعضاء المميزون على الصفقات **قبل عدة ساعات من الأعضاء المجانيين**. مع العلم أن صفقاتنا تستمر أحياناً ليوم واحد فقط، هذه ميزة ستضمن لك الاستفادة!',
      savingsTitle: 'وفر متوسط 200 ريال لكل تذكرة',
      savingsDesc: 'تسمح صفقاتنا بتوفير متوسط 200 ريال لكل تذكرة طيران. اشترِ تذكرة واحدة والاشتراك المميز يدفع تكلفته حرفياً (وحتى 10 أضعاف أكثر)! التوفير يكون أكبر عند السفر مع الأصدقاء أو العائلة.',
      happyMembers: 'أكثر من 500+ عضو سعيد',
      testimonial1: '"وفرت 300 ريال على رحلتي إلى لندن! العضوية المميزة دفعت تكلفتها في الشهر الأول."',
      testimonial2: '"أفضل استثمار للمسافرين المتكررين. الوصول المبكر للصفقات يغير قواعد اللعبة!"',
      testimonial3: '"التنبيهات الأسبوعية تبقيني على اطلاع بأفضل الأسعار. أوصي بشدة بالخطة المميزة!"',
      guaranteeTitle: 'ضمان استرداد الأموال لمدة 30 يوماً',
      guaranteeDesc: 'إذا لم تكن سعيداً باشتراكك المميز لأي سبب، نقدم استرداد كامل خلال أول 30 يوماً. لا يوجد مخاطرة في تجربة الخطة المميزة!',
      dontMiss: 'لا تفوت الصفقة المثالية',
      dontMissDesc: 'توقف عن إضاعة الساعات في البحث عن تذاكر الطيران. دعنا نجد لك أفضل الأسعار لرحلة أحلامك.',
      features: [
        { icon: Bell, text: 'تنبيهات أسبوعية بصفقات الطيران' },
        { icon: Star, text: 'الوصول إلى 100% من الصفقات الخاصة' },
        { icon: Zap, text: 'وصول مبكر - قبل الأعضاء المجانيين بساعات' },
        { icon: Gift, text: 'خصومات حصرية للأعضاء' },
        { icon: Shield, text: 'دعم واتساب مميز' }
      ]
    }
  };

  const content = t[language];

  return (
    <div className={`min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Header />

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 bg-clip-text text-transparent">
            {content.title}
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto">
            {content.subtitle}
          </p>
        </div>

        {/* Pricing Card */}
        <div className="max-w-md mx-auto mb-16">
          <div className="bg-white rounded-3xl shadow-2xl p-8 border-4 border-orange-500 relative overflow-hidden">
            {/* Popular Badge */}
            <div className="absolute top-0 right-0 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-6 py-2 transform translate-x-8 translate-y-4 rotate-45">
              <span className="text-xs font-bold">BEST</span>
            </div>

            <h3 className="text-3xl font-bold text-center mb-4">{content.premiumPlan}</h3>
            <div className="text-center mb-6">
              <div className="text-5xl font-bold bg-gradient-to-r from-orange-500 to-purple-600 bg-clip-text text-transparent">
                {content.price}
              </div>
              <div className="text-gray-600 text-lg">{content.perYear}</div>
              <div className="text-sm text-gray-500 mt-2">{content.billedAnnually}</div>
            </div>

            {/* Features List */}
            <div className="space-y-3 mb-6">
              {content.features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature.text}</span>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => navigate('/subscribe')}
              className="w-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white font-bold py-4 px-8 rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              {content.getStarted}
            </button>

            <p className="text-center text-sm text-gray-600 mt-4 font-semibold">
              {content.moneyBackGuarantee}
            </p>
            <p className="text-center text-xs text-gray-500 mt-2">
              {content.moneyBackDesc}
            </p>
          </div>
        </div>

        {/* Why Premium Section */}
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-4xl font-bold text-center mb-12">{content.whyPremium}</h2>

          {/* Receive All Deals */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8">
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="text-2xl md:text-3xl font-bold text-gray-800">{content.receiveAll}</span>
              <span className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                {content.allDeals}
              </span>
              <span className="text-2xl md:text-3xl font-bold text-gray-800">{content.theDeals}</span>
            </div>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              {content.receiveAllDesc}
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              {content.earlyAccess}
            </p>
          </div>

          {/* Savings Section */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl shadow-xl p-8 md:p-12 mb-8 border-2 border-green-200">
            <h3 className="text-3xl font-bold text-center mb-4 text-green-700">
              {content.savingsTitle}
            </h3>
            <p className="text-lg text-gray-700 text-center leading-relaxed">
              {content.savingsDesc}
            </p>
          </div>

          {/* Happy Members */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8">
            <h3 className="text-3xl font-bold text-center mb-8">{content.happyMembers}</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 italic">{content.testimonial1}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 italic">{content.testimonial2}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 italic">{content.testimonial3}</p>
              </div>
            </div>
          </div>

          {/* Guarantee Section */}
          <div className="bg-blue-50 rounded-2xl shadow-xl p-8 md:p-12 border-2 border-blue-200">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Shield className="w-12 h-12 text-blue-600" />
              <h3 className="text-3xl font-bold text-blue-700">{content.guaranteeTitle}</h3>
            </div>
            <p className="text-lg text-gray-700 text-center leading-relaxed">
              {content.guaranteeDesc}
            </p>
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 rounded-3xl p-12 text-center text-white shadow-2xl max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{content.dontMiss}</h2>
          <p className="text-xl mb-8 opacity-90">{content.dontMissDesc}</p>
          <button
            onClick={() => navigate('/subscribe')}
            className="bg-white text-purple-600 font-bold py-4 px-10 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
          >
            {content.getStarted} ✨
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PremiumPage;